import { getLocation } from '@koala/sdk/v4';
import { useQuery } from '@tanstack/react-query';
import { createHttpClient } from '@/services/client';
import { __DANGEROUS_EMPTY_REQUEST_ORIGIN, getOrigin } from '@/utils';
import { toResolvedLocationId } from '@/utils/locations';

/**
 * `id` is nullable because its most common source is `selectConveyance().location`,
 * which resolves to `basket?.location?.id ?? activeLocation?.detail?.id ?? null`.
 * The previous `id ?? 0` + `enabled: id !== undefined` pairing let that `null`
 * through and requested `store-locations/0`, the same defect TEAM-13663 fixed on
 * the menu route.
 */
export function useLocation(id: number | null | undefined) {
  let origin: string;

  if (typeof window !== 'undefined') {
    origin = getOrigin(window.location.host);
  } else {
    origin = __DANGEROUS_EMPTY_REQUEST_ORIGIN;
  }

  const client = createHttpClient({ origin });
  const resolvedId = toResolvedLocationId(id);

  return useQuery({
    queryKey: ['location', { id: resolvedId }],
    queryFn: () => {
      // `enabled` screens this; the throw keeps an unresolved id off the wire if
      // the query is ever refetched imperatively, which bypasses `enabled` in v4.
      if (resolvedId === undefined) {
        throw new Error(`useLocation requires a resolved location id, received ${String(id)}`);
      }

      return getLocation(
        {
          id: resolvedId,
          meta: {
            include: ['operating_hours', 'attributes', 'delivery_hours', 'business_hours'],
          },
        },
        { client },
      );
    },
    enabled: resolvedId !== undefined,
    refetchOnWindowFocus: false,
    staleTime: 3600000, // 60 minutes
  });
}
